/* eslint-disable react-hooks/exhaustive-deps */
import { useRef, useEffect, useState, useContext } from 'react';
import { saveAs } from 'file-saver';

/** Common Components */
import CardSection from '../../../../components/ui/card/CardSection';
import FormCreator from '../../../../components/finalForms/FormCreator';
import Buttons from '../../../../components/ui/button/Buttons';
import MolGrid, { Column } from '@moltech/molui/molgrid';
import moment from 'moment';
import { findFieldsById } from '../../../../components/finalForms/libs/manageFields';
import ToastService from '../../../../services/toastService/ToastService';
import SwalAlert from '../../../../services/swalService/SwalService';

/** data */
import { PaymentSearchFields } from './Components/PaymentSearchFilters.data';

/** CSS Files */
import "../../Financial.scss";

/**Wrapped Component */
import Financial from '../../Financial';

/**Utilities */
import { customRowsPerPageDropdown, getDropdownValue } from '../../../../components/finalForms/libs/data/dropdownUtilities';
import { manageFilteredDateTime } from '../../../../components/finalForms/libs/manageDateTime';
import { ServiceLocationContext } from '../../../../utils/context/ServiceLocationContext';

/** API call */
import { useDeleteInvoicePaymentMutation, useExportInvoicePaymentsMutation } from '../../../../app/services/financialsAPI';
import formatDate from '../../../../components/finalMolGrid/libs/formatDate';
import { useGetMolGridDataMutation } from '../../../../app/services/commonAPI';

const Payments = () => {

  const [formData, setFormData] = useState(PaymentSearchFields);
  const [searchdata, setSearchdata] = useState(null);
  const [isDisable, setIsDisable] = useState(true);
  const searchFormRef = useRef(null);
  const { confirm } = SwalAlert();

  const { selectedLocation } = useContext(ServiceLocationContext);
  const locationkey = getDropdownValue(selectedLocation);
  const currentDate = formatDate(new Date(), "MM-DD-YYYY");

  const gridRef = useRef();

  const [exportInvoicePayments, {
    isLoading: isExporting,
    isSuccess: isAllExported,
    data: exportAllData
  }] = useExportInvoicePaymentsMutation();

  const [deleteInvoicePayment, { isSuccess: isInvoicePaymentDeleted, data: deleteInvoicePaymentData }] =
    useDeleteInvoicePaymentMutation();


  useEffect(() => {
    const today = moment().toDate();
    const oneMonthAgo = moment().subtract(6, 'months').toDate();
    // Inject pre-filled values via initialState
    const modifiedSearchFields = {
      ...PaymentSearchFields,
      initialState: {
        ...PaymentSearchFields.initialState,
        dpaymentstartdate: oneMonthAgo,
        dpaymentenddate: today,
      },
    };
    const Dpaymentenddate = findFieldsById(modifiedSearchFields, "dpaymentenddate");
    if (Dpaymentenddate?.fieldSetting?.dateConfig) {
      Dpaymentenddate.fieldSetting.dateConfig.filteredDate = manageFilteredDateTime(oneMonthAgo, 'after', true);
    };
    const InvoiceEnddateField = findFieldsById(modifiedSearchFields, 'dinvoiceenddate');
    if (InvoiceEnddateField?.fieldSetting?.dateConfig) {
      InvoiceEnddateField.fieldSetting.dateConfig.filteredDate = null
    };
    setFormData(modifiedSearchFields);
  }, []);


  // Export All Details response handling
  useEffect(() => {
    if (!isExporting && isAllExported && exportAllData) {
      const blob = new Blob([exportAllData]);
      saveAs(blob, `Invoice Payments ${currentDate}.xlsx`);
    }
  }, [isExporting, isAllExported, exportAllData]);

  useEffect(() => {
    if (isInvoicePaymentDeleted) {
      ToastService.success("Invoice Payment Delete Successfully!");
      gridRef.current.reload();
    }
  }, [isInvoicePaymentDeleted && deleteInvoicePaymentData]);

  const handleStartdateChange = (dataField, formDatas) => {

    if (dataField === "dinvoicestartdate") {
      const { dinvoicestartdate, sinvoicenumber } = formDatas;
      const form = { ...formData };
      form.initialState = {
        ...formData.initialState, dinvoicestartdate: dinvoicestartdate, dinvoiceenddate: '', sinvoicenumber: sinvoicenumber
      };
      const InvoiceEnddateField = findFieldsById(form, 'dinvoiceenddate');
      if (InvoiceEnddateField?.fieldSetting?.dateConfig) {
        InvoiceEnddateField.fieldSetting.dateConfig.filteredDate = manageFilteredDateTime(dinvoicestartdate, 'after', true);
      };
      setFormData(form);
    }

    if (dataField === "dpaymentstartdate") {
      const { dpaymentstartdate } = formDatas;
      const form = { ...formData };
      const startDate = new Date(dpaymentstartdate);
      const endDate = new Date(startDate);
      endDate.setMonth(endDate.getMonth() + 6); // add 6 months
      form.initialState = {
        ...formData.initialState, dpaymentstartdate: dpaymentstartdate, dpaymentenddate: endDate
      };
      setFormData(form);
    }
  }

  const handleEnddateChange = (formDatas) => {
    const form = { ...formData };
    form.initialState = { ...formDatas }
    setFormData(form);
  };

  const handleFieldChange = (dataField, updatedFormData) => {
    if (dataField === 'dinvoicestartdate') {
      handleStartdateChange(dataField, updatedFormData)
    }
    if (dataField === 'dinvoiceenddate') {
      handleEnddateChange(updatedFormData)
    }
    if (dataField === "dpaymentstartdate") {
      handleStartdateChange(dataField, updatedFormData)
    }
    if (dataField === "dpaymentenddate") {
      //   handleEnddateChange(updatedFormData)
    }
  };

  const handleSearch = () => {
    const Searchformdata = searchFormRef.current.getFormData();
    setSearchdata(Searchformdata);
  }

  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.reload();
    }
  }, [locationkey, searchdata]);

  const beforeMolGridLoad = (reqPrm) => {
    const req = {
      pagination: {
        pageNumber: (reqPrm.first / reqPrm.rows) + 1,
        pageSize: reqPrm.rows
      },
      sortField: reqPrm.sortField,
      sortOrder: reqPrm.sortOrder,
      groupBy: reqPrm.groupBy ? reqPrm.groupBy : '',
      plServiceLocationKey: locationkey,
      pdtInvoiceStartDate: searchdata?.dinvoicestartdate || null,
      pdtInvoiceEndDate: searchdata?.dinvoiceenddate || null,
      pdtPaymentStartDate: searchdata?.dpaymentstartdate || formData.initialState.dpaymentstartdate ? formData.initialState.dpaymentstartdate : null,
      pdtPaymentEndDate: searchdata?.dpaymentenddate || formData?.initialState.dpaymentenddate ? formData.initialState.dpaymentenddate : null,
      psTranNumber: searchdata?.sinvoicenumber || null,
    };
    return req;
  }

  const afterMolGridLoad = (data) => {
    setIsDisable(false);
    return {
      data: data.dataSource,
      count: data.totalRecord
    };
  }

  const rowClass = (rowData) => {
    if (rowData.overShortAmount > 0) {
      return 'red-color-row';
    } else if (rowData.overShortAmount < 0) {
      return 'green-color-row';
    } else {
      return ''; // reset to default (no class)
    }
  };

  const DeleteInvoicePayment = (rowData) => {
    if (rowData?.lInvoicePaymentID) {
      confirm(
        "Delete?",
        "Are you sure you want to delete this payment?",
        "Delete",
        "Cancel"
      ).then((confirmed) => {
        if (confirmed) {
          deleteInvoicePayment(rowData?.lInvoicePaymentID);
        }
      });
    }
  }

  const getCtxMenuItems = (row) => {
    if (!row) return [];

    return [
      {
        label: "Delete Payment",
        // icon: 'pi pi-plus',
        command: () => DeleteInvoicePayment(row),
      },

    ];
  }

  const handleDownloadClick = () => {
    const request = {
      plServiceLocationKey: locationkey,
      pdtInvoiceStartDate: searchdata?.dinvoicestartdate || null,
      pdtInvoiceEndDate: searchdata?.dinvoiceenddate || null,
      pdtPaymentStartDate: searchdata?.dpaymentstartdate || null,
      pdtPaymentEndDate: searchdata?.dpaymentenddate || null,
      psTranNumber: searchdata?.sinvoicenumber || null
    }
    exportInvoicePayments(request);
  }

  return (
    <div className="supplier-po-table head-center payment-list-scroll small-card-btn-sec">

      <div className='form-card row my-2 mx-0'>
        <div className='col-11'>
          <div className='row'>
            <FormCreator
              ref={searchFormRef}
              config={formData}
              onColumnChange={handleFieldChange}
            />
          </div>
        </div>
        <div className='col-1'>
          <Buttons
            buttonText="Search"
            buttonTypeClassName="btn blue-btn w-full"
            onClick={handleSearch}
          />
        </div>
      </div>
      <div className='row px-0'>
        <div className='col-12'>
          <CardSection
            cardTitle={`Payments`}
            isButtonVisible={true}
            titleButtonClick={handleDownloadClick}
            rightButton={true}
            buttonClassName={'blue-btn mb-1'}
            titleText={'Export'}
            isIcon={true}
            iconClass="file-icons:microsoft-excel"
            isButtonLoading={isExporting}
            isButtonDisable={isDisable}
          >
            <div className="payments-list head-center small-card-btn-sec">
              <MolGrid ref={gridRef} url="/Financials/GetAllInvoicePayments" client={useGetMolGridDataMutation}
                beforeLoad={beforeMolGridLoad}
                afterLoad={afterMolGridLoad}
                grouping
                expandableRowGroups={true}

                scrollable
                scrollHeight="flex"
                filterDisplay="row"

                sortMode="single"
                showGridlines
                resizableColumns
                rowClassName={rowClass}
                paginator
                rowsPerPageOptions={[10, 25, 50, 100]}
                rows={99999}
                paginatorTemplate={{
                  layout: "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown",
                  RowsPerPageDropdown: (options) => {
                    return customRowsPerPageDropdown(options, options?.value)
                  }
                }}
                ctxMenuItems={getCtxMenuItems}
              >
                <Column field="sRepName" header="Sales Rep" grouping sortable />
                <Column field="sTranNumber" header="Invoice Number" bodyStyle={{ textAlign: "center" }} grouping sortable />
                <Column field="customer" header="Customer" grouping sortable />
                <Column field="dtTranDate" header="Invoice Date" colType="datetime" format="mm/dd/yyyy" grouping bodyStyle={{ textAlign: "center" }} sortable />
                <Column field="salesAmount" header="Sales Amt" colType="money" bodyStyle={{ textAlign: "right" }} sortable />
                <Column field="shippingAmount" header="Shipping" colType="money" bodyStyle={{ textAlign: "right" }} sortable />
                <Column field="taxAmount" header="Tax" colType="money" bodyStyle={{ textAlign: "right" }} sortable />
                <Column field="invoiceAmount" header="Invoice Amt" colType="money" bodyStyle={{ textAlign: "right" }} sortable />
                <Column field="dtPaymentDate" header="Payment Date" colType="datetime" format="mm/dd/yyyy" grouping bodyStyle={{ textAlign: "center" }} sortable />
                <Column field="paymentAmount" header="Payment" colType="money" bodyStyle={{ textAlign: "right" }} sortable />
                <Column field="writeOffAmount" header="Write Off" colType="money" bodyStyle={{ textAlign: "right" }} sortable />
                <Column field="overShortAmount" header="Over/Short" colType="money" bodyStyle={{ textAlign: "right" }} sortable />
              </MolGrid>
            </div>
          </CardSection>
        </div>
      </div>
    </div>
  )
}
export default Financial(Payments)